/**
 * /licenses — the Commercial License dashboard.
 *
 * From the monolith's `view === "license"` block. The page is a locked upsell state
 * or eight stacked sections, each of which now lives in src/components/license/ —
 * the status card, the live certificate preview, the rights grid, the summary table,
 * the timeline / download history / activity log, and the trust footer.
 *
 * SIMULATED. Nothing here is server-verified — see hooks/useLicense.ts.
 */
import { useNavigate } from 'react-router-dom';
import { BackLink } from '../components/ui';
import {
  CertificatePreview,
  LicenseAgreementHistory,
  LicenseHistory,
  LicensedSongsTable,
  LicenseLockedState,
  LicenseRightsGrid,
  LicenseStatusCard,
  LicenseSummaryTable,
  LicenseTrustFooter,
} from '../components/license';
import { useLicense } from '../hooks';
import { paths } from './paths';

export default function Licenses() {
  const navigate = useNavigate();
  const {
    ownsCommercial,
    license,
    licensedSongs,
    agreementHistory,
    licenseNumber,
    customerName,
    purchaseDate,
    version,
    updatedDate,
    verifyUrl,
    activity,
    downloadHistory,
    certificateFields,
    certificateZoomed,
    setCertificateZoomed,
    downloadCertificate,
    printCertificate,
    shareLicenseLink,
    copyLicenseNumber,
    selfVerify,
  } = useLicense();

  const issued = purchaseDate || '—';
  const ownerStatus = license?.status ?? 'active';

  /**
   * Jumps to the verification page with the account's own licence pre-checked.
   *
   * NAVIGATES EITHER WAY, and the `finally` is what makes that true. `selfVerify` no
   * longer rejects — `licenseStore.verify` catches and records `'failed'` — but this
   * function also calls `addActivity` and `setVerifyQuery` before it, and a throw between
   * them used to leave the user on this page with no indication that the button did
   * anything. The destination renders whichever of the five verification states resulted,
   * including the "Could not check" one, so arriving there is right in every case.
   */
  async function handleSelfVerify() {
    try {
      await selfVerify();
    } finally {
      navigate(paths.licenseVerify(licenseNumber));
    }
  }

  return (
    <div>
      {!ownsCommercial ? (
        <LicenseLockedState onGetLicense={() => navigate(paths.products)} />
      ) : (
        <>
          <BackLink fallbackTo={paths.settings} fallbackLabel="Settings" />

          <LicenseStatusCard
            licenseNumber={licenseNumber}
            issued={issued}
            version={version}
            status={ownerStatus}
            onCopyNumber={copyLicenseNumber}
            onDownload={downloadCertificate}
            onVerify={() => void handleSelfVerify()}
            onViewAgreement={() => navigate(paths.agreement)}
            onPrint={printCertificate}
          />

          <CertificatePreview
            version={version}
            fields={certificateFields}
            zoomed={certificateZoomed}
            onZoomChange={setCertificateZoomed}
            onDownload={downloadCertificate}
            onPrint={printCertificate}
            onShare={shareLicenseLink}
          />

          <LicenseRightsGrid />

          <LicenseSummaryTable
            customerName={customerName}
            issued={issued}
            updatedDate={updatedDate}
            verifyUrl={verifyUrl}
            status={ownerStatus}
            onCopyLink={shareLicenseLink}
          />

          <LicensedSongsTable songs={licensedSongs} />

          <LicenseAgreementHistory items={agreementHistory} />

          <LicenseHistory activity={activity} downloadHistory={downloadHistory} issued={issued} />

          <LicenseTrustFooter />
        </>
      )}
    </div>
  );
}
